import type { ConvexError } from 'convex/values';
import { ZodError } from 'zod';
import * as z from 'zod/v4/core';
import type { ApplicationError } from './applicationError';

/**
 * Extracts a user-friendly error message from various error types.
 * Handles: client-side ZodError, server-side ArgumentValidationError,
 * server-side Zod refinements, and application errors.
 */
export const getUserErrorMessage = (error: unknown): string => {
  /**
   * 1. Client-side `ZodError`
   *
   * @example
   * try {
   *   schema.parse(data); // Client-side validation before calling mutation
   *   await convex.mutation(...);
   * } catch (error) { ... }
   */
  if (error instanceof ZodError) {
    console.error('ZodError:', JSON.stringify(error.issues, null, 2));
    return prettifyError(error);
  }

  /**
   * 2. Server-side function argument validator
   *
   * Convex first checks that function args have the correct name and match the correct types.
   * Even though we use Zod validators, they are mapped to Convex validators internally using `zodToConvexFields(...)`.
   *
   * Example:
   * Say we have args like `id: z.number()`, then this maps to Convex validator like `v.float64()`.
   * If a string is passed to `id`, then Convex will throw a `ArgumentValidationError` noting `v.float64()` violation.
   *
   * This is an instance of `Error`, not `ConvexError`.
   */
  if (error instanceof Error && error.message.includes('ArgumentValidationError')) {
    // Convex logs the error message automatically in the browser console.
    return 'The data seems to be in the wrong format.';
  }

  /**
   * 3. Server-side function argument validator (Zod refinement failures)
   *
   * After Convex's `ArgumentValidationError` passes (basic types match), convex-helpers
   * runs Zod's full `.parse()` validation. This catches Zod-specific constraints that
   * can't be expressed as Convex validators.
   *
   * Take this example:
   * `z.string().min(5)` → Convex sees `v.string()` and passes the above step, now this function checks
   * the `.min(5)` which can't be expressed as Convex validator.
   *
   * Also thrown by `validateX()` in `packages/backend/convex/utils/errors.ts`.
   *
   * Returns a `ConvexError` with `{ data: { ZodError: ZodIssue[] } }`.
   * @see https://github.com/get-convex/convex-helpers/blob/main/packages/convex-helpers/server/zod4.ts#L889
   */
  if (isConvexZodError(error)) {
    const zodError = new ZodError(error.data.ZodError);
    // Even though Convex logs the error message automatically in the browser console,
    // logging the `ZodError` portion of the error improves readability.
    console.error('ZodError:', JSON.stringify(error.data.ZodError, null, 2));
    return prettifyError(zodError);
  }

  /**
   * 4. Server-side function errors explicitly thrown as `ApplicationError`
   *
   * These error always contain a `userErrorMessage` and optionally a `developerErrorMessage`.
   *
   * @example
   * // convex/someFile.ts
   * throw new ConvexError<ApplicationError>({
   *   extra: { ctxUser: ctx.user },
   *   developerErrorMessage: 'Platform globals is not configured',
   *   userErrorMessage: GENERIC_USER_ERROR_MESSAGE,
   * });
   */
  if (isApplicationError(error)) {
    console.error(
      `ApplicationError: ${error.data.userErrorMessage}`,
      error.data.developerErrorMessage,
    );
    return error.data.userErrorMessage;
  }

  /**
   *  5. If none of the above, then it's an unexpected error.
   *
   * @example
   * // convex/someFile.ts
   * throw new Error("Some bad thing happened");
   * throw new ConvexError("Some bad thing happened"); // Note that `ConvexError` on it own shouldn't be used though
   *
   * `throw new Error(..)` should be used for server errors that do not return to the client,
   * for example, errors within Convex scheduled jobs.
   */

  // Log the error to the browser console to handle the case it's not a Convex error.
  console.error('Unexpected error:', error);
  return 'Unexpected error occurred';
};

/**
 * Formats a ZodError using Zod 4's built-in prettifyError.
 * https://zod.dev/error-formatting?id=zprettifyerror
 * Strips checkmark symbols for cleaner toast messages.
 */
const prettifyError = (error: ZodError): string => {
  return z.prettifyError(error).replace(/✖ /g, '').trim();
};

// Type guards
type ConvexZodError = { data: { ZodError: z.$ZodIssue[] } };
const isConvexZodError = (e: unknown): e is ConvexZodError => {
  const err = e as { name?: string; data?: { ZodError?: unknown } };
  return err?.name === 'ConvexError' && Array.isArray(err?.data?.ZodError);
};

const isApplicationError = (e: unknown): e is ConvexError<ApplicationError> => {
  const err = e as { name?: string; data?: { userErrorMessage?: unknown } };
  return err?.name === 'ConvexError' && err.data?.userErrorMessage !== undefined;
};

/**
 * Extracts the developer error message from an ApplicationError.
 * Useful for logging to Sentry or other error tracking services.
 */
export const getDeveloperError = (error: unknown): string | undefined => {
  if (isApplicationError(error)) {
    return error.data.developerErrorMessage;
  }
  return undefined;
};
